import { hasBankWithdrawals } from '../../util/BankWithdrawals'
import { formatDistance } from 'date-fns'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container } from '../../components/Animations'
import CopyButton from '../../components/CopyButton'
import { InlinePill } from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'
import FiatRamps from '../../state/FiatRamps'
import Radom, { DepositAccount, DepositAccountType, Transaction } from '../../state/Radom'
import ConvertController from '../../state/ConvertController'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import { getMethod, getMethodByTicker, getMethodLogoByTicker, getPaymentMethods, prodNetworks, testNetworks } from '../../util/Managed'
import { formatAssetAmount, useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import {
  ALL_TRANSACTION_KINDS,
  ALL_TRANSACTION_STATUSES,
  AssetActivityRow,
  activityRowForAsset,
  formatMoney
} from './MoneyHome'
import {
  ActivityAmount,
  ActivityLabel,
  ActivityMain,
  ActivityRow,
  ActivityTime,
  AssetLogoCircle,
  CurrencySymbolGlyph,
  DetailLabel,
  DetailRow,
  DetailValue,
  DetailValueGroup,
  GhostViewAllButton,
  HeroTotal,
  InlineTextButton,
  MutedNote,
  PanelNote,
  PanelPillButton,
  PanelPillGlyph,
  RailOptionDesc,
  RailOptionMain,
  RailOptionRow,
  RailOptionTitle,
  RowAmount,
  RowAmountSub,
  RowIconCircle,
  RowMain,
  RowSub,
  RowTitle
} from './MoneyStyles'
import {
  BackLink,
  CurrencyActionsRow,
  CurrencyCard,
  CurrencyCardLabel,
  CurrencyHero,
  CurrencyIdentity,
  CurrencyLayout,
  CurrencyName
} from './CurrencyPageStyles'

const CASH_CODES: Record<string, { name: string, symbol: string }> = {
  EUR: { name: 'Euro', symbol: '€' },
  USD: { name: 'US dollar', symbol: '$' },
  GBP: { name: 'British pound', symbol: '£' }
}

type ActivityStatus = 'idle' | 'loading' | 'loaded' | 'error'

/**
 * One page per currency — cash or crypto. The Money home stays a summary;
 * everything about an asset (balance, where it's held, how money moves in and
 * out, its activity) lives here.
 */
const CurrencyPage = observer((): ReactElement => {
  const params = useParams<{ code: string }>()
  const navigate = useNavigate()
  const [testMode] = useTestMode()
  const { has, isLoaded: permissionsLoaded } = usePermissions()

  const code = (params.code ?? '').toUpperCase()
  const cash = CASH_CODES[code]
  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'

  const canReadBalance = has('ReadBalance')
  const canReadEvents = has('ReadEvents')
  const canReadDepositAccounts = has('ReadDepositAccount')

  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [activityStatus, setActivityStatus] = useState<ActivityStatus>('idle')
  const [fiatAccounts, setFiatAccounts] = useState<DepositAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)

  useEffect(() => {
    if (!permissionsLoaded) return

    if (canReadBalance) {
      void Treasury.loadBalances()
    }

    if (canReadEvents) {
      setActivityStatus('loading')
      Radom.listTransactions(
        0,
        100,
        ALL_TRANSACTION_KINDS,
        ALL_TRANSACTION_STATUSES,
        testMode ? testNetworks : prodNetworks,
        new Date(0).toISOString(),
        new Date().toISOString(),
        testMode
      )
        .then(response => {
          setTransactions(response.data)
          setActivityStatus('loaded')
        })
        .catch(err => {
          console.error('Failed to load currency activity', err)
          setActivityStatus('error')
        })
    }

    if (cash && code === 'USD' && canReadDepositAccounts) {
      setIsLoadingAccounts(true)
      Radom.listDepositAccounts(testMode, 0, 100, undefined, [DepositAccountType.Fiat])
        .then(response => setFiatAccounts(response.data))
        .catch(err => console.error('Failed to load fiat accounts', err))
        .finally(() => setIsLoadingAccounts(false))
    }
  }, [permissionsLoaded, testMode, code])

  /** Managed balances for this ticker, one row per network. */
  const holdings = useMemo(() => {
    return [...Treasury.managedBalances, ...Treasury.privateManagedBalances]
      .map(balance => {
        // Unified-treasury rows carry an asset ticker and no network.
        const unifiedTicker = balance.asset
        const method = unifiedTicker
          ? (getMethodByTicker(unifiedTicker) ?? { name: unifiedTicker, ticker: unifiedTicker })
          : getMethod(balance.network ?? '', balance.token)
        const isFiat = unifiedTicker
          ? !!CASH_CODES[unifiedTicker.toUpperCase()] || !!method.isFiat
          : balance.network === 'Fiat' || !!method.isFiat
        const ticker = unifiedTicker ?? (isFiat ? (balance.token ?? method.ticker) : method.ticker)
        return { balance, method, isFiat, ticker, isUnified: !!unifiedTicker }
      })
      .filter(entry => entry.ticker.toUpperCase() === code)
      .filter(entry => entry.isUnified
        // Unified rows aggregate mainnet inventory; they are never testnet.
        ? !testMode
        : testMode ? !!entry.method.isTestnet || entry.isFiat : !entry.method.isTestnet)
      .map(entry => {
        const amount = Number(entry.balance.balance)
        const quote = Treasury.priceQuotes.find(q => q.from.toLowerCase() === entry.ticker.toLowerCase())
        return {
          key: entry.isUnified
            ? `asset-${entry.ticker}`
            : `${entry.balance.network}-${entry.balance.token ?? ''}-${entry.balance.treasuryId ?? ''}`,
          networkLabel: entry.isUnified
            ? 'Unified balance'
            : entry.isFiat ? entry.ticker : entry.method.hostChain?.name ?? entry.method.name,
          amount,
          fiatValue: entry.isFiat && entry.ticker === defaultCurrency ? amount : quote ? amount * quote.value : 0,
          hasQuote: !!quote || (entry.isFiat && entry.ticker === defaultCurrency)
        }
      })
      .sort((a, b) => b.amount - a.amount)
  }, [Treasury.managedBalances, Treasury.privateManagedBalances, Treasury.priceQuotes, code, testMode])

  const totalBalance = holdings.reduce((sum, row) => sum + row.amount, 0)
  const totalFiatValue = holdings.reduce((sum, row) => sum + row.fiatValue, 0)
  const hasQuote = holdings.some(row => row.hasQuote)

  const method = getPaymentMethods().find(m => m.ticker === code && !m.hostChain && !m.isFiat) ??
    getPaymentMethods().find(m => m.ticker === code && !m.isFiat)
  const displayName = cash?.name ?? method?.currencyName ?? method?.name ?? code
  const logo = cash ? undefined : getMethodLogoByTicker(code)

  const bastionUsdAccount =
    fiatAccounts.find(account =>
      !!account.bastionId && !!account.accountDetails.Fiat?.accountType.us && account.status === 'active') ??
    fiatAccounts.find(account => !!account.bastionId && !!account.accountDetails.Fiat?.accountType.us)
  const usdBankWithdrawals = !!User.organization?.bankWithdrawals?.usdAssets.length

  const activityRows = useMemo((): AssetActivityRow[] => transactions
    .map(transaction => activityRowForAsset(transaction, code))
    .filter((row): row is AssetActivityRow => !!row)
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 25), [transactions, code])

  const renderRailOption = (
    title: string,
    description: string,
    pill: { tone: 'green' | 'amber' | 'neutral', label: string } | null,
    onClick?: () => void
  ): ReactElement => (
    <RailOptionRow key={title} $muted={!onClick}>
      <RailOptionMain>
        <RailOptionTitle>{title}</RailOptionTitle>
        <RailOptionDesc>{description}</RailOptionDesc>
      </RailOptionMain>
      {onClick
        ? <PanelPillButton type='button' onClick={onClick}>Open</PanelPillButton>
        : pill && <InlinePill $tone={pill.tone}>{pill.label}</InlinePill>}
    </RailOptionRow>
  )

  const renderActions = (): ReactElement => {
    if (!cash) {
      return <CurrencyActionsRow>
        <PanelPillButton $primary type='button' onClick={() => navigate(`/receive?asset=${encodeURIComponent(code)}`)}>
          <PanelPillGlyph aria-hidden='true'>↓</PanelPillGlyph> Deposit {code}
        </PanelPillButton>
        <PanelPillButton type='button' onClick={() => navigate('/send')}>
          <PanelPillGlyph aria-hidden='true'>↑</PanelPillGlyph> Send
        </PanelPillButton>
        <PanelPillButton type='button' onClick={() => ConvertController.open(code)}>
          <PanelPillGlyph aria-hidden='true'>⇄</PanelPillGlyph> Convert
        </PanelPillButton>
      </CurrencyActionsRow>
    }

    if (code === 'EUR') {
      return <CurrencyActionsRow>
        {FiatRamps.cashDepositEnabled && <PanelPillButton $primary type='button' onClick={() => navigate('/receive?rail=cash')}>
          <PanelPillGlyph aria-hidden='true'>↓</PanelPillGlyph> Add funds
        </PanelPillButton>}
        {(FiatRamps.cashWithdrawalEnabled || hasBankWithdrawals(User.organization?.bankWithdrawals)) && <PanelPillButton type='button' onClick={() => navigate('/send')}>
          <PanelPillGlyph aria-hidden='true'>↑</PanelPillGlyph> Withdraw to your bank
        </PanelPillButton>}
      </CurrencyActionsRow>
    }

    if (code === 'USD') {
      // Mirrors the EUR pair. Each side is enabled by its own Bastion
      // capability: a virtual account for pay-ins, the USD wire rail for
      // withdrawals.
      return <CurrencyActionsRow>
        <PanelPillButton
          $primary
          type='button'
          disabled={!bastionUsdAccount}
          title={bastionUsdAccount ? undefined : 'Not enabled for this organization'}
          onClick={() => navigate('/receive?rail=cash&currency=USD')}>
          <PanelPillGlyph aria-hidden='true'>↓</PanelPillGlyph> Add funds
        </PanelPillButton>
        <PanelPillButton
          type='button'
          disabled={!usdBankWithdrawals}
          title={usdBankWithdrawals ? undefined : 'Not enabled for this organization'}
          onClick={() => navigate('/send')}>
          <PanelPillGlyph aria-hidden='true'>↑</PanelPillGlyph> Withdraw to your bank
        </PanelPillButton>
      </CurrencyActionsRow>
    }

    return <></>
  }

  const renderUsdAccountCard = (): ReactElement | null => {
    if (code !== 'USD') return null

    if (isLoadingAccounts) {
      return <CurrencyCard>
        <CurrencyCardLabel>Virtual account</CurrencyCardLabel>
        <LoadingBar style={{ borderRadius: 8, height: 34, width: '100%' }} />
      </CurrencyCard>
    }

    if (!canReadDepositAccounts || !bastionUsdAccount) {
      return null
    }

    const fiat = bastionUsdAccount.accountDetails.Fiat
    const us = fiat?.accountType.us

    return <CurrencyCard>
      <CurrencyCardLabel>Virtual account · pay-ins</CurrencyCardLabel>
      {us?.accountNumber && <DetailRow>
        <DetailLabel>Account number</DetailLabel>
        <DetailValueGroup>
          <DetailValue $mono>{us.accountNumber}</DetailValue>
          <CopyButton text={us.accountNumber} />
        </DetailValueGroup>
      </DetailRow>}
      {us?.routingNumber && <DetailRow>
        <DetailLabel>Routing number</DetailLabel>
        <DetailValueGroup>
          <DetailValue $mono>{us.routingNumber}</DetailValue>
          <CopyButton text={us.routingNumber} />
        </DetailValueGroup>
      </DetailRow>}
      {us?.bankBeneficiaryName && <DetailRow>
        <DetailLabel>Beneficiary</DetailLabel>
        <DetailValueGroup>
          <DetailValue>{us.bankBeneficiaryName}</DetailValue>
          <CopyButton text={us.bankBeneficiaryName} />
        </DetailValueGroup>
      </DetailRow>}
      {fiat?.bankName && <DetailRow>
        <DetailLabel>Bank name</DetailLabel>
        <DetailValueGroup>
          <DetailValue>{fiat.bankName}</DetailValue>
          <CopyButton text={fiat.bankName} />
        </DetailValueGroup>
      </DetailRow>}
      {fiat?.bankAddress && <DetailRow>
        <DetailLabel>Bank address</DetailLabel>
        <DetailValueGroup>
          <DetailValue>{fiat.bankAddress}</DetailValue>
          <CopyButton text={fiat.bankAddress} />
        </DetailValueGroup>
      </DetailRow>}
      <PanelNote>
        Payments sent to this account arrive as USDC in your balance. USD can&apos;t be
        withdrawn from this account. Send or convert the USDC instead.
      </PanelNote>
      <InlineTextButton
        type='button'
        onClick={() => navigate('/virtual_accounts')}
        style={{ alignSelf: 'flex-start' }}>
        View all virtual accounts
      </InlineTextButton>
    </CurrencyCard>
  }

  const renderCapabilitiesCard = (): ReactElement | null => {
    if (!cash) return null

    if (code === 'EUR') {
      return <CurrencyCard>
        <CurrencyCardLabel>Ways to move EUR</CurrencyCardLabel>
        <PanelNote>
          EUR is never held as EUR today: bank transfers in arrive as USDC, and
          withdrawals sell USDC before your bank receives EUR.
        </PanelNote>
        {FiatRamps.cashDepositEnabled && renderRailOption(
          'Instant bank payment',
          'Approve a payment in your banking app. Arrives as USDC',
          null,
          () => navigate('/receive?rail=cash&currency=EUR&method=openBanking')
        )}
        {FiatRamps.cashDepositEnabled && renderRailOption(
          'SEPA bank transfer',
          'Wire EUR with your payment reference. Arrives as USDC',
          null,
          () => navigate('/receive?rail=cash&currency=EUR&method=sepaMemo')
        )}
        {(FiatRamps.cashWithdrawalEnabled || hasBankWithdrawals(User.organization?.bankWithdrawals)) && renderRailOption(
          'Withdraw to your bank',
          'We sell USDC and your bank receives EUR',
          null,
          () => navigate('/send')
        )}
        {renderRailOption(
          'Virtual IBAN',
          'Your own EUR account details, holding a real EUR balance',
          { tone: 'amber', label: 'Coming soon' }
        )}
      </CurrencyCard>
    }

    if (code === 'USD') {
      return <CurrencyCard>
        <CurrencyCardLabel>Ways to move USD</CurrencyCardLabel>
        {!bastionUsdAccount && renderRailOption(
          'Virtual account',
          'Dedicated USD account & routing number for pay-ins',
          { tone: 'neutral', label: 'Not available in your region' }
        )}
        {renderRailOption(
          'Withdraw to your bank',
          'We sell USDC or USDT and your bank receives USD by wire',
          usdBankWithdrawals
            ? null
            : { tone: 'neutral', label: 'Not enabled for this organization' },
          usdBankWithdrawals ? () => navigate('/send') : undefined
        )}
        {!(bastionUsdAccount && usdBankWithdrawals) && <MutedNote style={{ paddingTop: 8 }}>
          Operating in the US? Contact sales to enable USD accounts and bank withdrawals for this organization.
        </MutedNote>}
      </CurrencyCard>
    }

    return <CurrencyCard>
      <CurrencyCardLabel>Ways to move GBP</CurrencyCardLabel>
      {renderRailOption(
        'Virtual account',
        'Dedicated GBP account details for receiving bank transfers',
        { tone: 'neutral', label: 'Not available in your region' }
      )}
      {renderRailOption(
        'Instant bank payment',
        'Fund your balance straight from a GBP bank account',
        { tone: 'neutral', label: 'Not available in your region' }
      )}
      {renderRailOption(
        'Withdraw to your bank',
        'Sell USDC and receive GBP in your bank account',
        { tone: 'neutral', label: 'Not available in your region' }
      )}
      <MutedNote style={{ paddingTop: 8 }}>
        GBP rails are rolling out region by region. Contact sales to register interest.
      </MutedNote>
    </CurrencyCard>
  }

  const renderActivityCard = (): ReactElement => (
    <CurrencyCard>
      <CurrencyCardLabel>Activity</CurrencyCardLabel>
      {!canReadEvents && <MutedNote>Your role does not have permission to view transactions.</MutedNote>}
      {canReadEvents && (activityStatus === 'idle' || activityStatus === 'loading') &&
        <LoadingBar style={{ borderRadius: 8, height: 34, width: '100%' }} />}
      {canReadEvents && activityStatus === 'error' &&
        <MutedNote>Couldn&apos;t load activity. Refresh to try again.</MutedNote>}
      {canReadEvents && activityStatus === 'loaded' && activityRows.length === 0 &&
        <MutedNote>No {code} activity yet.</MutedNote>}
      {canReadEvents && activityStatus === 'loaded' && activityRows.map(row =>
        <ActivityRow key={row.id}>
          <ActivityMain>
            <ActivityLabel>{row.label}</ActivityLabel>
            <ActivityTime>{formatDistance(row.timestamp, Date.now(), { addSuffix: true })}</ActivityTime>
          </ActivityMain>
          <ActivityAmount $direction={row.direction}>{row.amountLabel}</ActivityAmount>
        </ActivityRow>)}
      {canReadEvents && activityStatus === 'loaded' && activityRows.length > 0 &&
        <GhostViewAllButton to='/transactions'>View all in Transactions →</GhostViewAllButton>}
    </CurrencyCard>
  )

  const balancesLoading = !permissionsLoaded || (canReadBalance && Treasury.loading)

  return <Container>
    <CurrencyLayout>
      <BackLink type='button' onClick={() => navigate('/')}>← Home</BackLink>

      <CurrencyHero>
        <CurrencyIdentity>
          {cash
            ? <RowIconCircle style={{ height: 44, width: 44 }}>
              <CurrencySymbolGlyph style={{ fontSize: 18 }}>{cash.symbol}</CurrencySymbolGlyph>
            </RowIconCircle>
            : <AssetLogoCircle $logo={logo} style={{ height: 44, width: 44 }} />}
          <RowMain>
            <RowTitle style={{ fontSize: 16 }}>{code}</RowTitle>
            <CurrencyName>{displayName}</CurrencyName>
          </RowMain>
        </CurrencyIdentity>

        {balancesLoading
          ? <LoadingBar style={{ borderRadius: 10, height: 44, maxWidth: 320 }} />
          : cash && totalBalance <= 0
            // Fiat balances aren't a supported surface yet: this page is about
            // the currency's rails, so no placeholder zero is shown.
            ? null
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <HeroTotal style={{ fontSize: 34 }}>
                {cash ? formatMoney(totalBalance, code) : `${formatAssetAmount(totalBalance, code)} ${code}`}
              </HeroTotal>
              {!cash && hasQuote &&
                <RowAmountSub style={{ fontSize: 13.5 }}>
                  ≈ {formatMoney(totalFiatValue, defaultCurrency)}
                </RowAmountSub>}
            </div>}

        {renderActions()}
      </CurrencyHero>

      {renderUsdAccountCard()}
      {renderCapabilitiesCard()}
      {renderActivityCard()}
    </CurrencyLayout>
  </Container>
})

export default CurrencyPage
